import { Link } from '@inertiajs/react'
import { ArrowRight, FileText } from 'lucide-react'
import { cn } from '@/lib/utils'
import ProjectTaskMenu from '@/frontend/features/Fde/ProjectTaskMenu'
import { TaskPeople, TaskStatusIcon, taskDate, taskStatusTone } from '@/frontend/features/Fde/projectTaskPresentation'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { TaskSurfaceProps } from '@/frontend/features/Fde/ProjectTaskCanvas'
import type { ProjectTask } from '@/frontend/features/Fde/types'

interface Props extends TaskSurfaceProps {
  task: ProjectTask
  variant: 'focus' | 'attention' | 'delivered'
  projectId: string
  today: string
  sourcePaths: string[]
  compact?: boolean
}

export default function ProjectSummaryTask(props: Props) {
  const { task, variant, projectId, sourcePaths, copy } = props
  const words = copy.management
  const focus = variant === 'focus'
  const delivered = variant === 'delivered'
  const overdue = props.overdueIds.has(task.id)
  const href = fdeProjectPath(`${fdeRoutes.projectTasks}/${task.id}`, projectId)
  const due = task.due_on ? (task.due_on === props.today ? words.current_month : taskDate(task.due_on)) : null
  const people = <TaskPeople people={task.assignees} emptyLabel={words.task_unassigned} workStateLabels={words.task_work_states} />
  const deadline = task.due_on ? (
    <time dateTime={task.due_on} className={cn('text-xs tabular-nums', overdue ? 'text-destructive' : 'text-muted-foreground')}>
      {!focus && `${overdue ? words.summary_overdue : words.task_due_on} `}{due}
    </time>
  ) : <span className="text-xs text-muted-foreground">{words.no_due_date}</span>

  return (
    <article className={cn(focus && 'overflow-hidden rounded-lg border border-border bg-background')}>
      <ProjectTaskMenu {...props} onSelect={() => props.onSelect(task.id)}>
        <Link href={href} className={cn('group block outline-none transition-colors hover:bg-muted/30 focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring', focus ? 'p-5 sm:p-6' : 'px-4 py-3')}>
          {props.compact ? (
            <div className="flex items-start gap-2.5">
              <span title={words.statuses[task.status]} aria-label={words.statuses[task.status]} className={cn('mt-0.5 shrink-0 rounded-full', taskStatusTone[task.status])}><TaskStatusIcon status={task.status} className="size-3.5" /></span>
              <div className="min-w-0 flex-1">
                <h3 title={task.title} className="line-clamp-2 text-[13px] font-medium leading-5 group-hover:text-primary">{task.title}</h3>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
                  <span className="font-mono">{task.identifier}</span>
                  <span className="truncate">{words.statuses[task.status]}</span>
                  {task.due_on && <time dateTime={task.due_on} className={cn('tabular-nums', overdue && 'text-destructive')} title={overdue ? words.summary_overdue : words.task_due_on}>{taskDate(task.due_on)}</time>}
                </div>
              </div>
              <TaskPeople people={task.assignees} emptyLabel={words.task_unassigned} compact workStateLabels={words.task_work_states} />
            </div>
          ) : delivered ? (
            <div className="flex items-start gap-3 sm:items-center">
              <TaskStatusIcon status="done" className="mt-0.5 size-4 shrink-0 text-emerald-600 sm:mt-0" />
              <div className="min-w-0 flex-1">
                <h3 className="line-clamp-2 text-[13px] font-medium leading-5 group-hover:text-primary">{task.title}</h3>
                <span className="mt-1 block font-mono text-[10px] text-muted-foreground">{task.identifier}</span>
                <div className="mt-2 sm:hidden">{people}</div>
              </div>
              <div className="hidden w-40 shrink-0 sm:block">{people}</div>
              {task.completed_at && <time dateTime={task.completed_at} className="shrink-0 text-[11px] tabular-nums text-muted-foreground">{taskDate(task.completed_at.slice(0, 10))}</time>}
              <ArrowRight className="hidden size-3.5 shrink-0 text-muted-foreground/50 transition-colors group-hover:text-foreground sm:block" />
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] text-muted-foreground">{task.identifier}</span>
                <span className={cn('inline-flex items-center gap-1.5 rounded px-2 py-1 text-[11px]', taskStatusTone[task.status])}><TaskStatusIcon status={task.status} className="size-3" />{words.statuses[task.status]}</span>
              </div>
              <h3 className={cn('mt-3 line-clamp-2 font-medium group-hover:text-primary', focus ? 'max-w-xl text-lg leading-7 tracking-tight sm:text-xl' : 'text-[13px] leading-5')}>{task.title}</h3>
              {focus ? (
                <div className="mt-7 flex items-end justify-between gap-4 border-t border-border/70 pt-4">
                  <div className="grid min-w-0 flex-1 gap-4 sm:grid-cols-2">
                    <div className="min-w-0"><p className="mb-1.5 text-[11px] text-muted-foreground">{words.task_responsibles}</p>{people}</div>
                    <div><p className="mb-1.5 text-[11px] text-muted-foreground">{overdue ? words.summary_overdue : words.task_due_on}</p>{deadline}</div>
                  </div>
                  <ArrowRight className="mb-1 size-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" />
                </div>
              ) : <div className="mt-3 flex flex-wrap items-center justify-between gap-2">{people}{deadline}</div>}
            </>
          )}
        </Link>
      </ProjectTaskMenu>
      {sourcePaths.length > 0 && (
        <div className="flex flex-wrap gap-x-4 gap-y-2 px-4 pb-4 sm:pl-11">
          {sourcePaths.map((path) => <Link key={path} href={fdeProjectPath(fdeRoutes.material, projectId, { file: path })} title={path} className="flex max-w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-primary"><FileText className="size-3 shrink-0" /><span className="truncate">{path.split('/').pop()}</span></Link>)}
        </div>
      )}
    </article>
  )
}
